import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Mail, Linkedin, Twitter, Globe, Calendar } from "lucide-react";
import { speakersAPI } from "../services/api";
import "./SpeakerDetail.css";

const SpeakerDetail = () => {
  const { id, eventId } = useParams();
  const navigate = useNavigate();
  const [speaker, setSpeaker] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSpeaker();
  }, [id]);

  const fetchSpeaker = async () => {
    setLoading(true);
    try {
      const response = await speakersAPI.getById(id);
      setSpeaker(response.data);
    } catch (error) {
      console.error("Failed to fetch speaker:", error);
      setSpeaker({
        id,
        name: "Dr. Anita Sharma",
        title: "CEO",
        organization: "Education Innovation",
        bio: "Dr. Anita Sharma is a renowned education reformist with over 20 years of experience in transforming educational institutions. She has led multiple initiatives across Asia and Africa, impacting millions of students. Her work focuses on integrating technology with traditional teaching methods to create inclusive learning environments.",
        photo: null,
        email: "anita@example.com",
        linkedin: "https://linkedin.com/in/anitasharma",
        twitter: "https://twitter.com/anitasharma",
        website: "https://anitasharma.com",
        sessions: [
          {
            id: "1",
            title: "Opening Keynote: Future of Education",
            type: "keynote",
            start_time: "2025-01-20T09:00:00",
            location: "Main Auditorium",
          },
          {
            id: "5",
            title: "Panel: Women in Leadership",
            type: "panel",
            start_time: "2025-01-21T14:00:00",
            location: "Hall B",
          },
        ],
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="speaker-detail-page">
        <div className="speaker-detail-skeleton">
          <div className="skeleton avatar-skeleton" />
          <div
            className="skeleton"
            style={{ height: 28, width: "60%", margin: "16px auto 8px" }}
          />
          <div
            className="skeleton"
            style={{ height: 18, width: "40%", margin: "0 auto" }}
          />
        </div>
      </div>
    );
  }

  if (!speaker) {
    return (
      <div className="speaker-detail-page">
        <div className="empty-state">
          <h3>Speaker not found</h3>
          <p>This speaker may have been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="speaker-detail-page">
      <div className="speaker-hero">
        <div className="speaker-avatar-large">
          {speaker.photo ? (
            <img src={speaker.photo} alt={speaker.name} />
          ) : (
            speaker.name?.charAt(0)
          )}
        </div>
        <h1 className="speaker-name">{speaker.name}</h1>
        <p className="speaker-title">{speaker.title}</p>
        {speaker.organization && (
          <p className="speaker-org">{speaker.organization}</p>
        )}
      </div>

      {(speaker.email ||
        speaker.linkedin ||
        speaker.twitter ||
        speaker.website) && (
        <div className="speaker-social">
          {speaker.email && (
            <a href={`mailto:${speaker.email}`} className="social-btn">
              <Mail size={20} />
            </a>
          )}
          {speaker.linkedin && (
            <a
              href={speaker.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="social-btn"
            >
              <Linkedin size={20} />
            </a>
          )}
          {speaker.twitter && (
            <a
              href={speaker.twitter}
              target="_blank"
              rel="noopener noreferrer"
              className="social-btn"
            >
              <Twitter size={20} />
            </a>
          )}
          {speaker.website && (
            <a
              href={speaker.website}
              target="_blank"
              rel="noopener noreferrer"
              className="social-btn"
            >
              <Globe size={20} />
            </a>
          )}
        </div>
      )}

      <section className="speaker-section">
        <h2>About</h2>
        <p className="speaker-bio">{speaker.bio}</p>
      </section>

      {speaker.sessions && speaker.sessions.length > 0 && (
        <section className="speaker-section">
          <h2>Sessions ({speaker.sessions.length})</h2>
          <div className="speaker-sessions">
            {speaker.sessions.map((session) => (
              <div
                key={session.id}
                className="session-mini-card"
                onClick={() =>
                  navigate(
                    eventId
                      ? `/event/${eventId}/session/${session.id}`
                      : `/session/${session.id}`,
                  )
                }
              >
                <span className="session-type-tag">{session.session_type}</span>
                <h4>{session.title}</h4>
                <div className="session-meta">
                  <Calendar size={14} />
                  <span>
                    {formatDate(session.start_time)} at{" "}
                    {formatTime(session.start_time)}
                  </span>
                </div>
                <div className="session-location">{session.location}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default SpeakerDetail;
